import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { billingAPI, type BalanceTransactionItem, type PublicUser } from '../../utils/api';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import TopUpModal from './TopUpModal';
import './Balance.css';

const TRANSACTIONS_PAGE_SIZE = 4;

function formatMoney(value: number): string {
  const prefix = value > 0 ? '+ ' : value < 0 ? '- ' : '';
  return `${prefix}${Math.abs(value).toLocaleString('ru-RU')} ₽`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const Balance: React.FC = () => {
  const { setUser } = useAuth();
  const { openSubscriptionModal } = useSubscription();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [monthlyPrice, setMonthlyPrice] = useState(499);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<BalanceTransactionItem[]>([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsHasMore, setTransactionsHasMore] = useState(false);
  const [transactionsLoadingMore, setTransactionsLoadingMore] = useState(false);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [error, setError] = useState('');

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await billingAPI.getSummary();
      if (response.success) {
        setBalance(response.balance);
        setMonthlyPrice(response.monthly_price);
        setHasSubscription(response.has_subscription);
        setSubscriptionExpiresAt(response.subscription_expires_at);
        setTransactions(response.transactions);
        setTransactionsTotal(response.transactions_total);
        setTransactionsHasMore(response.transactions_total > response.transactions.length);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить данные баланса');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const loadMoreTransactions = async () => {
    try {
      setTransactionsLoadingMore(true);
      const response = await billingAPI.getTransactions({
        limit: TRANSACTIONS_PAGE_SIZE,
        offset: transactions.length,
      });
      if (response.success) {
        setTransactions((prev) => [...prev, ...response.transactions]);
        setTransactionsHasMore(response.has_more);
        setTransactionsTotal(response.total);
      }
    } finally {
      setTransactionsLoadingMore(false);
    }
  };

  const handleTopUpSuccess = (nextBalance: number, updatedUser?: PublicUser) => {
    setBalance(nextBalance);
    if (updatedUser) {
      setUser(updatedUser);
    }
    void loadSummary();
  };

  return (
    <div className="balance-page">
      <Header />

      <main className="balance-main">
        <div className="container">
          <nav className="balance-breadcrumbs" aria-label="Навигация">
            <Link to="/dashboard">Главная</Link>
            <span aria-hidden="true">/</span>
            <span>Баланс</span>
          </nav>

          <header className="balance-header">
            <h1 className="balance-header__title">Баланс</h1>
            <p className="balance-header__subtitle">
              Управляйте балансом, просматривайте платежи и историю расходов
            </p>
          </header>

          {error && <p className="balance-error">{error}</p>}

          <div className="balance-grid">
            <section className="balance-card balance-card--current">
              <div className="balance-card__top">
                <div>
                  <h2 className="balance-card__label">Текущий баланс</h2>
                  <p className="balance-card__amount">
                    {loading ? '—' : `${balance.toLocaleString('ru-RU')} ₽`}
                  </p>
                  <p className="balance-card__hint">Доступно для оплаты и услуг</p>
                </div>
                <button
                  type="button"
                  className="balance-card__primary-btn"
                  onClick={() => setIsTopUpOpen(true)}
                >
                  Пополнить баланс
                </button>
              </div>

              <div className="balance-card__meta">
                <div className="balance-card__meta-item">
                  <span className="balance-card__meta-label">Автопополнение</span>
                  <span className="balance-card__meta-value">Выкл</span>
                </div>
                <div className="balance-card__meta-item">
                  <span className="balance-card__meta-label">Следующее списание</span>
                  <span className="balance-card__meta-value">
                    {hasSubscription && subscriptionExpiresAt
                      ? `${formatShortDate(subscriptionExpiresAt)} · ${monthlyPrice.toLocaleString('ru-RU')} ₽`
                      : '—'}
                  </span>
                </div>
              </div>
            </section>

            <section className="balance-card balance-card--payments">
              <div className="balance-card__row-header">
                <h2 className="balance-card__title">Быстрые действия</h2>
                <button type="button" className="balance-card__link-btn" disabled>
                  + Добавить
                </button>
              </div>
              <div className="balance-payments-empty">
                Способы оплаты пока не подключены
              </div>
              <button type="button" className="balance-card__footer-link" disabled>
                Все способы оплаты
                <span aria-hidden="true">⌄</span>
              </button>
            </section>

            <section className="balance-card balance-card--transactions">
              <div className="balance-card__row-header">
                <h2 className="balance-card__title">История транзакций</h2>
                <button
                  type="button"
                  className="balance-card__link-btn"
                  onClick={() => void loadMoreTransactions()}
                  disabled={transactionsLoadingMore || transactionsTotal === 0}
                >
                  Все транзакции
                </button>
              </div>

              {loading ? (
                <p className="balance-card__loading">Загрузка…</p>
              ) : transactions.length === 0 ? (
                <p className="balance-card__empty">Транзакций пока нет</p>
              ) : (
                <ul className="balance-transactions">
                  {transactions.map((tx) => (
                    <li key={tx.id} className="balance-transaction">
                      <div className="balance-transaction__icon" aria-hidden="true">
                        <img src={`${process.env.PUBLIC_URL}/icons/modals/people.svg`} alt="" />
                      </div>
                      <div className="balance-transaction__content">
                        <span className="balance-transaction__title">{tx.description}</span>
                        <span className="balance-transaction__date">{formatDateTime(tx.created_at)}</span>
                      </div>
                      <div className="balance-transaction__right">
                        <span
                          className={`balance-transaction__amount ${
                            tx.amount >= 0 ? 'balance-transaction__amount--plus' : 'balance-transaction__amount--minus'
                          }`}
                        >
                          {formatMoney(tx.amount)}
                        </span>
                        <span className="balance-transaction__status">Успешно</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {transactionsHasMore && !loading && (
                <button
                  type="button"
                  className="balance-card__more"
                  onClick={() => void loadMoreTransactions()}
                  disabled={transactionsLoadingMore}
                >
                  {transactionsLoadingMore ? 'Загрузка…' : (
                    <>
                      Показать еще
                      <span aria-hidden="true">⌄</span>
                    </>
                  )}
                </button>
              )}
            </section>

            <div className="balance-side">
              <section className="balance-card balance-card--tariff">
                <h2 className="balance-card__title">Текущий тариф</h2>
                <div className="balance-tariff">
                  <div className="balance-tariff__head">
                    <div>
                      <p className="balance-tariff__name">Профессиональный</p>
                      <p className="balance-tariff__price">
                        {monthlyPrice.toLocaleString('ru-RU')} ₽ / месяц
                      </p>
                    </div>
                    {hasSubscription && (
                      <span className="balance-tariff__badge">Активен</span>
                    )}
                  </div>
                  <p className="balance-tariff__next">
                    {hasSubscription && subscriptionExpiresAt
                      ? `Следующее списание ${formatShortDate(subscriptionExpiresAt)}`
                      : 'Подписка не активна'}
                  </p>
                </div>
                <button
                  type="button"
                  className="balance-card__nav-link"
                  onClick={openSubscriptionModal}
                >
                  Управление тарифом
                  <span aria-hidden="true">›</span>
                </button>
              </section>

              <section className="balance-card balance-card--docs">
                <h2 className="balance-card__title balance-card__title--small">
                  Счета и закрывающие документы
                </h2>
                <p className="balance-card__text">
                  Получайте акты выполненных работ и закрывающие документы по оплатам
                </p>
                <button type="button" className="balance-card__nav-link" disabled>
                  Перейти к документам
                  <span aria-hidden="true">›</span>
                </button>
              </section>
            </div>
          </div>
        </div>
      </main>

      {isTopUpOpen && (
        <TopUpModal
          currentBalance={balance}
          onClose={() => setIsTopUpOpen(false)}
          onSuccess={handleTopUpSuccess}
        />
      )}

      <Footer />
    </div>
  );
};

export default Balance;
